import { ArticleJsonLd } from 'next-seo'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { ISbStoryData } from 'storyblok-js-client'
import styled from 'styled-components'

import { Seo } from 'components/Seo'
import { CMSImage } from 'components/blocks/deprecated/CMSImage'
import { Editable } from 'components/blocks/deprecated/Editable'
import { Layout } from 'components/blocks/layout/Layout'
import { Empty } from 'components/blocks/sections/directories/AllEntries/Empty'
import { Loader } from 'components/blocks/sections/directories/AllEntries/Loader'
import { ContentThumb } from 'components/blocks/sections/directories/thumbs/ContentThumb'
import { Tag } from 'components/ui'
import { TagList } from 'components/ui/TagList'
import { ButtonLink } from 'components/ui/deprecated/ButtonLink'
import { Container } from 'components/ui/deprecated/Layout'
import { SocialShare } from 'components/ui/deprecated/SocialShare'
import { Text } from 'components/ui/deprecated/Text'
import { SITE_ORIGIN } from 'lib/constants'
import { useDataContext } from 'lib/contexts/dataContext'
import { useTranslation } from 'lib/i18n'
import { Storyblok } from 'lib/storyblok'
import { PatientStoryStoryblok } from 'lib/storyblok/types'
import {
  getStoryblokImageAttributes,
  getStoryblokCacheValue,
  setStoryblokCacheValue,
} from 'lib/utils/content'
import { formatDateLocale } from 'lib/utils/datetime'
import { removeTrailingSlash } from 'lib/utils/string'

import { StoryblokRichText } from '../rich-text/StoryblokRichText'
import { getTags } from './BlogPostPage'

type Props = {
  story: ISbStoryData<PatientStoryStoryblok>
}

export const PatientStoryPage = ({ story }: Props): JSX.Element => {
  const { i18n, locale } = useTranslation()
  const sideBarRef = useRef<HTMLDivElement>(null)
  const [sideBarHeight, setSidebarHeight] = useState<number>(0)
  const { config } = useDataContext()
  const {
    query: { lang },
    isPreview,
  } = useRouter()

  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<
    ISbStoryData<PatientStoryStoryblok>[] | null
  >(null)
  const [error, setError] = useState(false)

  useEffect(function handleSidebarHeight() {
    const onResize = () => {
      if (sideBarRef?.current?.clientHeight) {
        setSidebarHeight(sideBarRef?.current?.clientHeight)
      }
    }

    onResize()
    window.addEventListener('resize', onResize)

    return () => window.removeEventListener('resize', onResize)
  }, [])

  const categories =
    story.content.categories && getTags(story.content.categories)

  const fetch = useCallback(async () => {
    setLoading(true)
    setError(false)

    try {
      const {
        data: { stories, cv },
      } = await Storyblok.get('cdn/stories', {
        page: 1,
        per_page: 3,
        version: isPreview ? 'draft' : 'published',
        starts_with: `${lang}/`,
        sort_by: 'first_published_at:desc',
        content_type: 'patient-story',
        resolve_relations: 'patient-story.categories',
        cv: getStoryblokCacheValue(),
        excluding_ids: story.id.toString(),
      })

      setData(stories)
      setStoryblokCacheValue(cv)
    } catch {
      setError(true)
    }

    setLoading(false)
  }, [isPreview, story])

  useEffect(() => {
    fetch()
  }, [fetch])

  return (
    <Layout>
      <Seo story={story} />
      <ArticleJsonLd
        title={
          story.content?.seo?.title
            ? JSON.stringify(story.content.seo.title).slice(1, -1)
            : ''
        }
        authorName="TFP"
        publisherName="TFP"
        publisherLogo={config?.content.menu_logo?.filename || ''}
        description={
          story.content?.seo?.description
            ? JSON.stringify(story.content.seo.description).slice(1, -1)
            : ''
        }
        datePublished={story.created_at}
        url={
          story.content?.canonical_url ||
          removeTrailingSlash(`${SITE_ORIGIN}${story.full_slug}`)
        }
        images={
          story.content?.seo?.og_image || story.content?.seo?.twitter_image
            ? [
                // we're not adding both because probably they will be the same and it's not
                // very professional to have two very similar images
                (story.content?.seo?.og_image ||
                  story.content?.seo?.twitter_image) as string,
              ]
            : []
        }
      />

      <Wrapper className="mt-20">
        {story.content.cover_image && (
          <StyledCMSImage
            width={
              getStoryblokImageAttributes({
                filename: story.content.cover_image.filename,
              }).width
            }
            height={
              getStoryblokImageAttributes({
                filename: story.content.cover_image.filename,
              }).height
            }
            image={story.content.cover_image}
            objectposition={story.content.object_position}
            transformOption="1600x480/smart/filters:format(webp)"
          />
        )}

        <InnerWrapper>
          <Sidebar ref={sideBarRef}>
            <Container>
              <WrapperSocialShare>
                <Text as="span" variant="sixteen">
                  {i18n('share')}:
                </Text>
                <SocialShare className="mt-3" />
              </WrapperSocialShare>
            </Container>
          </Sidebar>
          <Header sidebarHeight={sideBarHeight || 0}>
            <Tags>
              <TagList tags={categories} maxVisibleTags={1} hasHiddenTags />

              {story.first_published_at && (
                <Text as="span" variant="fourteen">
                  {formatDateLocale(story.first_published_at, locale)}
                </Text>
              )}
            </Tags>
            <Text as="h1" variant="title" className="mb-8">
              {story.content.title}
            </Text>
            <SocialShare />
          </Header>
          <ContentHolder>
            {story.content.content && (
              <Editable block={story.content}>
                <StoryblokRichText document={story.content.content} />
              </Editable>
            )}

            <Container className="mt-5" variant="typography">
              <Text as="span" variant="sixteen">
                {i18n('likeTheArticle')}
              </Text>
              <SocialShare className="mt-3" />
            </Container>
          </ContentHolder>
        </InnerWrapper>
      </Wrapper>
      <Container>
        {loading && !data?.length && <Loader />}
        {data?.length ? (
          <WrapperTumbs>
            <WrapperSection>
              <Tag>{i18n('successStoriesTag')}</Tag>

              <WrapperSectionTitle>
                <Text as="h3" variant="title/large">
                  {i18n('blogPostsSectionTitle')}
                </Text>
                <ButtonLink
                  variant="ghost"
                  href={`/${lang}/blog`}
                  className="w-fit"
                >
                  {i18n('blogPostsSectionSeeAll')}
                </ButtonLink>
              </WrapperSectionTitle>
            </WrapperSection>
            <BlogPostsGrid>
              {data?.map((item) => (
                <ContentThumb item={item} key={item.id} />
              ))}
            </BlogPostsGrid>
          </WrapperTumbs>
        ) : (
          <Empty className="w-full" isError={error} />
        )}
      </Container>
    </Layout>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const InnerWrapper = styled.div`
  margin-top: 2.5rem;
  width: 100%;

  ${({ theme }) => theme.media.xl} {
    margin-top: 3.75rem;
  }
`

const WrapperSocialShare = styled.div`
  max-width: fit-content;
  background-color: ${({ theme }) => theme.colors.palette.white};
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  box-shadow: ${({ theme }) => theme.shadows.small};
`

const Tags = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 1.25rem;
  gap: 12px;
`

const StyledCMSImage = styled(CMSImage)`
  width: 100%;
  margin: 0 auto;
  max-height: 30rem;
  object-fit: cover;
  object-position: ${({ objectposition }) => objectposition || 'center'};
`

const Header = styled.div<{ sidebarHeight: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;

  max-width: 49rem;
  margin: 0 auto;
  margin-bottom: 2.5rem;
  padding: 0rem 1.25rem;

  text-align: center;

  margin-top: 0;

  ${({ theme }) => theme.media.xl} {
    margin-bottom: 3.75rem;
    margin-top: ${({ sidebarHeight }) => `-${sidebarHeight}px`};
  }
`

const ContentHolder = styled.div`
  position: relative;
  z-index: ${({ theme }) => theme.zIndex.mid};
`

const Sidebar = styled.div`
  margin-top: 1rem;
  position: sticky;
  top: 5rem;
  display: none;
  max-width: fit-content;

  z-index: ${({ theme }) => theme.zIndex.mid};

  ${({ theme }) => theme.media.xl} {
    display: block;
    margin-top: 0rem;
  }
`

export const WrapperTumbs = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3rem;
  z-index: 2;
  width: 100%;
  padding-top: 2.5rem;
  padding-bottom: 2.5rem;

  ${({ theme }) => theme.media.md} {
    padding-bottom: 3.75rem;
  }
`

const BlogPostsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  column-gap: 1.25rem;
  row-gap: 3rem;
  z-index: 2;

  ${({ theme }) => theme.media.md} {
    grid-template-columns: repeat(2, 1fr);
  }

  ${({ theme }) => theme.media.lg} {
    grid-template-columns: repeat(3, 1fr);
  }
`

const WrapperSection = styled.div`
  display: flex;
  flex-direction: column;
`

const WrapperSectionTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  justify-content: space-between;
  margin-top: 1rem;

  ${({ theme }) => theme.media.lg} {
    flex-direction: row;
  }
`
